import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Create axios instance with interceptors
const createApiClient = (token) => {
  const client = axios.create({
    baseURL: API,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (token) {
    client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  return client;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('pos_token'));
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('pos_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [organization, setOrganization] = useState(() => {
    const saved = localStorage.getItem('pos_org');
    return saved ? JSON.parse(saved) : null;
  });
  const [branches, setBranches] = useState(() => {
    const saved = localStorage.getItem('pos_branches');
    return saved ? JSON.parse(saved) : [];
  });
  const [currentBranchId, setCurrentBranchId] = useState(() => localStorage.getItem('pos_branch_id'));
  const [featureFlags, setFeatureFlags] = useState({});
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(() => localStorage.getItem('pos_is_admin') === 'true');
  const [loading, setLoading] = useState(false);

  const api = createApiClient(token);

  // Persist state changes
  useEffect(() => {
    if (token) {
      localStorage.setItem('pos_token', token);
    } else {
      localStorage.removeItem('pos_token');
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      localStorage.setItem('pos_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('pos_user');
    }
  }, [user]);

  useEffect(() => {
    if (organization) {
      localStorage.setItem('pos_org', JSON.stringify(organization));
    } else {
      localStorage.removeItem('pos_org');
    }
  }, [organization]);

useEffect(() => {
  const safeBranches = Array.isArray(branches) ? branches : [];
  if (safeBranches.length > 0) {
    localStorage.setItem('pos_branches', JSON.stringify(safeBranches));
  } else {
    localStorage.removeItem('pos_branches');
  }
}, [branches]);

  useEffect(() => {
    if (currentBranchId) {
      localStorage.setItem('pos_branch_id', currentBranchId);
    } else {
      localStorage.removeItem('pos_branch_id');
    }
  }, [currentBranchId]);

  useEffect(() => {
    localStorage.setItem('pos_is_admin', isPlatformAdmin.toString());
  }, [isPlatformAdmin]);

  // Platform admin login
  const platformLogin = async (email, password) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/platform/login`, { email, password });
      const { access_token, admin } = response.data;
      setToken(access_token);
      setUser(admin);
      setIsPlatformAdmin(true);
      setOrganization(null);
      setBranches([]);
      setCurrentBranchId(null);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    } finally {
      setLoading(false);
    }
  };

  // Tenant login
  const tenantLogin = async (email, password) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/tenant/login`, { email, password });
      const { access_token, user: userData, organization: orgData, branches: branchesData } = response.data;
      setToken(access_token);
      setUser(userData);
      setOrganization(orgData);
      setBranches(branchesData);
      setIsPlatformAdmin(false);
      
      // Auto-select branch if only one
      if (branchesData.length === 1) {
        await selectBranch(branchesData[0].id, access_token);
      }
      
      return { success: true, branches: branchesData };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    } finally {
      setLoading(false);
    }
  };

  // Select branch
  const selectBranch = async (branchId, existingToken = null) => {
    const tokenToUse = existingToken || token;
    try {
      const response = await axios.post(
        `${API}/tenant/select-branch`,
        { branch_id: branchId },
        { headers: { Authorization: `Bearer ${tokenToUse}` } }
      );
      setToken(response.data.access_token);
      setCurrentBranchId(branchId);
      
      // Load feature flags
      const flagsResponse = await axios.get(`${API}/tenant/features`, {
        headers: { Authorization: `Bearer ${response.data.access_token}` }
      });
      setFeatureFlags(flagsResponse.data.flags);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to select branch' };
    }
  };

  // Logout
  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setOrganization(null);
    setBranches([]);
    setCurrentBranchId(null);
    setFeatureFlags({});
    setIsPlatformAdmin(false);
    localStorage.clear();
  }, []);

  // Check if feature is enabled
  const hasFeature = useCallback((featureKey) => {
    return featureFlags[featureKey] === true;
  }, [featureFlags]);

  // Check user role
  const hasRole = useCallback((...roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  }, [user]);

  // Get current branch
const safeBranches = Array.isArray(branches) ? branches : [];
const currentBranch = safeBranches.find(b => b.id === currentBranchId) || null;
  const value = {
    token,
    user,
    organization,
    branches,
    currentBranchId,
    currentBranch,
    featureFlags,
    isPlatformAdmin,
    loading,
    api,
    platformLogin,
    tenantLogin,
    selectBranch,
    logout,
    hasFeature,
    hasRole,
    isAuthenticated: !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
